import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { applyBranding } from "@/hooks/useBranding";
import { Button } from "@/components/ui/button";
import { Loader2, Briefcase, UserRound, Sparkles, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

const redirectFor = (role) => (role === "admin" ? "/admin" : role === "recruiter" ? "/recruiter" : "/candidate");

export default function SocialCallback() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const { loginWithGoogleSession, adoptToken, selectRole } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const processed = useRef(false);
  const [phase, setPhase] = useState("processing"); // processing | choose-role | error
  const [busy, setBusy] = useState(false);

  useEffect(() => { applyBranding({ brand_primary: "#10B981" }); }, []);

  const finish = (u) => {
    toast.success(`Welcome, ${(u.name || "there").split(" ")[0]}`);
    nav(redirectFor(u.role), { replace: true });
  };

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = new URLSearchParams((loc.hash || "").replace(/^#/, ""));
    const sessionId = hash.get("session_id");
    const token = hash.get("token");
    const err = hash.get("error");
    window.history.replaceState(null, "", loc.pathname);
    (async () => {
      if (err) { setPhase("error"); toast.error(err); return; }
      // Linking a second provider to an already signed-in account
      const linkMode = sessionStorage.getItem("hs_link_mode");
      if (linkMode === "google" && sessionId) {
        sessionStorage.removeItem("hs_link_mode");
        const res = await loginWithGoogleSession(sessionId, true);
        if (res.ok) { toast.success("Google connected to your account"); nav("/account", { replace: true }); }
        else { setPhase("error"); toast.error(res.error || "Could not connect Google"); }
        return;
      }
      let res;
      if (sessionId) res = await loginWithGoogleSession(sessionId);
      else if (token) res = await adoptToken(token, hash.get("needs_role") === "1");
      else { setPhase("error"); return; }
      if (!res.ok) { setPhase("error"); toast.error(res.error || "Sign-in failed"); return; }
      if (res.needs_role) {
        // If the user chose Candidate/Employer on the login screen, honour it
        // automatically instead of asking again — never silently defaults.
        const intended = sessionStorage.getItem("hs_intended_role");
        sessionStorage.removeItem("hs_intended_role");
        if (intended === "candidate" || intended === "recruiter") {
          const sr = await selectRole(intended);
          if (sr.ok) { finish(sr.user); return; }
        }
        setPhase("choose-role");
      } else finish(res.user);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseRole = async (role) => {
    setBusy(true);
    const res = await selectRole(role);
    setBusy(false);
    if (res.ok) finish(res.user);
    else toast.error(res.error);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#050807] px-4">
      <div className="pointer-events-none absolute inset-0 grain-overlay opacity-40" />
      <div className="pointer-events-none absolute -top-1/4 left-1/3 h-[36rem] w-[36rem] rounded-full bg-emerald-500/20 blur-[140px] animate-pulse" />
      <div className="pointer-events-none absolute -bottom-1/4 right-1/4 h-[30rem] w-[30rem] rounded-full bg-teal-600/15 blur-[140px]" />

      {phase === "processing" && (
        <div className="relative text-center space-y-5" data-testid="social-processing">
          <div className="relative h-16 w-16 mx-auto">
            <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
            <div className="relative h-16 w-16 rounded-full bg-primary/15 flex items-center justify-center"><Sparkles className="h-7 w-7 text-primary" /></div>
          </div>
          <div className="text-slate-200 font-display text-lg flex items-center gap-2 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Signing you in…</div>
        </div>
      )}

      {phase === "choose-role" && (
        <div className="relative w-full max-w-lg space-y-8" data-testid="role-chooser">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-primary/10 text-primary"><Sparkles className="h-3.5 w-3.5" /> One quick thing</div>
            <h1 className="text-3xl font-display font-extrabold tracking-tight text-white">How will you use HireStream?</h1>
            <p className="text-slate-400 text-sm">Pick the workspace that fits you — you can always switch later.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <button onClick={() => chooseRole("candidate")} disabled={busy} data-testid="role-candidate"
              className="group text-left rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-primary/60 hover:bg-primary/[0.06] transition-all disabled:opacity-50">
              <div className="h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"><UserRound className="h-6 w-6 text-primary" /></div>
              <div className="font-display font-semibold text-white text-lg">I'm looking for a job</div>
              <p className="text-sm text-slate-400 mt-1">Upload your CV once and let AI match you to every role that fits.</p>
            </button>
            <button onClick={() => chooseRole("recruiter")} disabled={busy} data-testid="role-recruiter"
              className="group text-left rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-primary/60 hover:bg-primary/[0.06] transition-all disabled:opacity-50">
              <div className="h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"><Briefcase className="h-6 w-6 text-primary" /></div>
              <div className="font-display font-semibold text-white text-lg">I'm hiring</div>
              <p className="text-sm text-slate-400 mt-1">Draft roles, build assessments and rank candidates with AI.</p>
            </button>
          </div>
          {busy && <div className="text-center text-slate-400 text-sm flex items-center gap-2 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Setting up your workspace…</div>}
        </div>
      )}

      {phase === "error" && (
        <div className="relative text-center space-y-4 max-w-sm" data-testid="social-error">
          <div className="h-14 w-14 rounded-2xl bg-rose-500/15 flex items-center justify-center mx-auto"><TriangleAlert className="h-7 w-7 text-rose-400" /></div>
          <div className="text-white font-display text-xl">Sign-in didn't complete</div>
          <p className="text-slate-400 text-sm">The link may have expired. Please try signing in again.</p>
          <Button onClick={() => nav("/login", { replace: true })} data-testid="back-to-login">Back to sign in</Button>
        </div>
      )}
    </div>
  );
}
